import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, X, Check, ExternalLink, AlertTriangle } from 'lucide-react';
import { get, post, patch, del } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Textarea } from '@/components/atoms/Textarea';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Badge } from '@/components/atoms/Badge';
import { toast } from '@/store/toastStore';
import { confirmModal } from '@/store/confirmStore';
import { cn } from '@/lib/cn';

/**
 * 어드민 — 외부 API 키 발급/만료 관리 (super 전용).
 *  - 실제 키 값은 .env 단일 진실 → 이 화면에는 발급/만료/메모만 저장.
 *  - 만료일 임박(30일 이내) 시 경고 색상 강조.
 */

interface Row {
  id: number;
  name: string;
  display_name: string;
  provider: string;
  env_key: string | null;
  console_url: string | null;
  issued_at: string | null;
  expires_at: string | null;
  rotation_cycle_days: number | null;
  notes: string | null;
  sort_order: number;
  days_left: number | null;
  created_at: string;
  updated_at: string;
}

type EditableField =
  | 'displayName'
  | 'provider'
  | 'envKey'
  | 'consoleUrl'
  | 'issuedAt'
  | 'expiresAt'
  | 'rotationCycleDays'
  | 'notes'
  | 'sortOrder';

const WARN_DAYS = 30;
const CRIT_DAYS = 7;

function fmtExpiry(row: Row): { label: string; tone: 'fall' | 'editor' | 'sub' } {
  if (!row.expires_at) return { label: '무기한', tone: 'sub' };
  const d = row.days_left ?? 0;
  if (d < 0) return { label: `만료 ${Math.abs(d)}일 경과`, tone: 'fall' };
  if (d <= CRIT_DAYS) return { label: `${d}일 남음`, tone: 'fall' };
  if (d <= WARN_DAYS) return { label: `${d}일 남음`, tone: 'editor' };
  return { label: `${d}일 남음`, tone: 'sub' };
}

export function ApiCredentialsPage() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'api-credentials'],
    queryFn: () => get<{ rows: Row[] }>('/admin/api-credentials'),
  });

  // 신규 등록
  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState({
    name: '',
    displayName: '',
    provider: '',
    envKey: '',
    consoleUrl: '',
    issuedAt: '',
    expiresAt: '',
    rotationCycleDays: '',
    notes: '',
  });

  const createMut = useMutation({
    mutationFn: () =>
      post<{ id: number }>('/admin/api-credentials', {
        name: newDraft.name.trim(),
        displayName: newDraft.displayName.trim(),
        provider: newDraft.provider.trim(),
        envKey: newDraft.envKey.trim() || null,
        consoleUrl: newDraft.consoleUrl.trim() || null,
        issuedAt: newDraft.issuedAt || null,
        expiresAt: newDraft.expiresAt || null,
        rotationCycleDays: newDraft.rotationCycleDays
          ? Number(newDraft.rotationCycleDays)
          : null,
        notes: newDraft.notes.trim() || null,
      }),
    onSuccess: () => {
      toast.success('API 키 정보를 추가했어요.');
      setCreating(false);
      setNewDraft({
        name: '',
        displayName: '',
        provider: '',
        envKey: '',
        consoleUrl: '',
        issuedAt: '',
        expiresAt: '',
        rotationCycleDays: '',
        notes: '',
      });
      qc.invalidateQueries({ queryKey: ['admin', 'api-credentials'] });
    },
    onError: (err: any) => toast.error(err?.message ?? '등록에 실패했어요.'),
  });

  // 인라인 편집
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Record<EditableField, string>>({
    displayName: '',
    provider: '',
    envKey: '',
    consoleUrl: '',
    issuedAt: '',
    expiresAt: '',
    rotationCycleDays: '',
    notes: '',
    sortOrder: '',
  });

  function startEdit(row: Row) {
    setEditingId(row.id);
    setDraft({
      displayName: row.display_name,
      provider: row.provider,
      envKey: row.env_key ?? '',
      consoleUrl: row.console_url ?? '',
      issuedAt: row.issued_at ?? '',
      expiresAt: row.expires_at ?? '',
      rotationCycleDays: row.rotation_cycle_days != null ? String(row.rotation_cycle_days) : '',
      notes: row.notes ?? '',
      sortOrder: String(row.sort_order),
    });
  }

  const updateMut = useMutation({
    mutationFn: (id: number) =>
      patch(`/admin/api-credentials/${id}`, {
        displayName: draft.displayName.trim() || undefined,
        provider: draft.provider.trim() || undefined,
        envKey: draft.envKey.trim() || null,
        consoleUrl: draft.consoleUrl.trim() || null,
        issuedAt: draft.issuedAt || null,
        expiresAt: draft.expiresAt || null,
        rotationCycleDays: draft.rotationCycleDays
          ? Number(draft.rotationCycleDays)
          : null,
        notes: draft.notes.trim() || null,
        sortOrder: draft.sortOrder ? Number(draft.sortOrder) : undefined,
      }),
    onSuccess: () => {
      toast.success('저장했어요.');
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ['admin', 'api-credentials'] });
    },
    onError: (err: any) => toast.error(err?.message ?? '저장에 실패했어요.'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => del(`/admin/api-credentials/${id}`),
    onSuccess: () => {
      toast.success('삭제했어요.');
      qc.invalidateQueries({ queryKey: ['admin', 'api-credentials'] });
    },
    onError: (err: any) => toast.error(err?.message ?? '삭제에 실패했어요.'),
  });

  async function onDelete(row: Row) {
    const ok = await confirmModal({
      title: '항목 삭제',
      message: `"${row.display_name}" 정보를 삭제할까요?\n실제 .env 키 값은 영향 받지 않아요.`,
      confirmLabel: '삭제',
      tone: 'danger',
    });
    if (ok) deleteMut.mutate(row.id);
  }

  function onCreateSubmit(e: FormEvent) {
    e.preventDefault();
    if (!newDraft.name.trim() || !newDraft.displayName.trim() || !newDraft.provider.trim()) {
      toast.error('식별자(name) / 표시명 / 공급자는 필수예요.');
      return;
    }
    createMut.mutate();
  }

  const rows = data?.rows ?? [];
  const warnCount = rows.filter(
    (r) => r.days_left != null && r.days_left <= WARN_DAYS,
  ).length;

  return (
    <div className="w-full">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-extrabold">API 키 유효기간</h1>
          <p className="mt-1 text-sm text-[var(--color-text-sub)]">
            실제 키 값은 <code className="rounded bg-[var(--color-bg)] px-1.5 py-0.5 text-xs">.env</code> 에서만 관리하고,
            여기서는 발급일/만료일/메모만 추적해요.
          </p>
        </div>
        <Button onClick={() => setCreating((v) => !v)} variant={creating ? 'ghost' : 'primary'}>
          {creating ? (
            <>
              <X className="mr-1 h-4 w-4" /> 취소
            </>
          ) : (
            <>
              <Plus className="mr-1 h-4 w-4" /> 새 API 등록
            </>
          )}
        </Button>
      </div>

      {warnCount > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-[var(--color-warn)] bg-[var(--color-warn-bg)] px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-[var(--color-warn)]" />
          <span>
            만료 {WARN_DAYS}일 이내인 API 키가 <b>{warnCount}개</b> 있어요. 갱신 일정을 확인해주세요.
          </span>
        </div>
      )}

      {/* 신규 등록 폼 */}
      {creating && (
        <form
          onSubmit={onCreateSubmit}
          className="mb-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <FieldText label="식별자 (kebab-case)" required value={newDraft.name}
              onChange={(v) => setNewDraft({ ...newDraft, name: v })}
              placeholder="예: stripe-secret" />
            <FieldText label="표시명" required value={newDraft.displayName}
              onChange={(v) => setNewDraft({ ...newDraft, displayName: v })}
              placeholder="예: Stripe Secret Key" />
            <FieldText label="공급자" required value={newDraft.provider}
              onChange={(v) => setNewDraft({ ...newDraft, provider: v })}
              placeholder="예: Stripe" />
            <FieldText label=".env 변수명" value={newDraft.envKey}
              onChange={(v) => setNewDraft({ ...newDraft, envKey: v })}
              placeholder="STRIPE_SECRET_KEY" />
            <FieldText label="콘솔 URL" value={newDraft.consoleUrl}
              onChange={(v) => setNewDraft({ ...newDraft, consoleUrl: v })}
              placeholder="https://dashboard.stripe.com/apikeys" />
            <FieldText label="권장 갱신 주기 (일)" type="number" value={newDraft.rotationCycleDays}
              onChange={(v) => setNewDraft({ ...newDraft, rotationCycleDays: v })}
              placeholder="예: 365" />
            <FieldText label="발급일" type="date" value={newDraft.issuedAt}
              onChange={(v) => setNewDraft({ ...newDraft, issuedAt: v })} />
            <FieldText label="만료일" type="date" value={newDraft.expiresAt}
              onChange={(v) => setNewDraft({ ...newDraft, expiresAt: v })} />
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs text-[var(--color-text-sub)]">메모</label>
              <Textarea
                rows={3}
                value={newDraft.notes}
                onChange={(e) => setNewDraft({ ...newDraft, notes: e.target.value })}
                placeholder="갱신 절차, 콘솔 경로 등"
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
              취소
            </Button>
            <Button type="submit" loading={createMut.isPending}>
              등록
            </Button>
          </div>
        </form>
      )}

      {/* 목록 */}
      {isLoading ? (
        <Skeleton className="h-80" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          {/* PC 헤더 */}
          <div className="hidden grid-cols-[1.5fr_1fr_1fr_1fr_120px] gap-3 border-b border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-2 text-xs font-semibold text-[var(--color-text-sub)] md:grid">
            <span>API</span>
            <span>발급일</span>
            <span>만료일</span>
            <span>남은 기간 / 주기</span>
            <span className="text-right">관리</span>
          </div>
          {rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-[var(--color-text-sub)]">
              등록된 API 키가 없어요.
            </div>
          ) : (
            <ul>
              {rows.map((row) => {
                const exp = fmtExpiry(row);
                const isEditing = editingId === row.id;
                return (
                  <li
                    key={row.id}
                    className="border-b border-[var(--color-border)] px-4 py-3 last:border-b-0"
                  >
                    {isEditing ? (
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="md:col-span-2 text-xs text-[var(--color-text-sub)]">
                          식별자(name): <code className="rounded bg-[var(--color-bg)] px-1.5 py-0.5">{row.name}</code> · 변경 불가
                        </div>
                        <FieldText label="표시명" value={draft.displayName}
                          onChange={(v) => setDraft({ ...draft, displayName: v })} />
                        <FieldText label="공급자" value={draft.provider}
                          onChange={(v) => setDraft({ ...draft, provider: v })} />
                        <FieldText label=".env 변수명" value={draft.envKey}
                          onChange={(v) => setDraft({ ...draft, envKey: v })} />
                        <FieldText label="콘솔 URL" value={draft.consoleUrl}
                          onChange={(v) => setDraft({ ...draft, consoleUrl: v })} />
                        <FieldText label="발급일" type="date" value={draft.issuedAt}
                          onChange={(v) => setDraft({ ...draft, issuedAt: v })} />
                        <FieldText label="만료일" type="date" value={draft.expiresAt}
                          onChange={(v) => setDraft({ ...draft, expiresAt: v })} />
                        <FieldText label="권장 갱신 주기 (일)" type="number" value={draft.rotationCycleDays}
                          onChange={(v) => setDraft({ ...draft, rotationCycleDays: v })} />
                        <FieldText label="정렬 순서" type="number" value={draft.sortOrder}
                          onChange={(v) => setDraft({ ...draft, sortOrder: v })} />
                        <div className="md:col-span-2">
                          <label className="mb-1 block text-xs text-[var(--color-text-sub)]">메모</label>
                          <Textarea
                            rows={2}
                            value={draft.notes}
                            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                          />
                        </div>
                        <div className="md:col-span-2 flex justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                            <X className="mr-1 h-4 w-4" /> 취소
                          </Button>
                          <Button
                            size="sm"
                            loading={updateMut.isPending && updateMut.variables === row.id}
                            onClick={() => updateMut.mutate(row.id)}
                          >
                            <Check className="mr-1 h-4 w-4" /> 저장
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-[1.5fr_1fr_1fr_1fr_120px] md:items-start md:gap-3">
                        {/* API 정보 */}
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-[var(--color-text)]">
                              {row.display_name}
                            </span>
                            <Badge variant="outline">{row.provider}</Badge>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--color-text-sub)]">
                            <code>{row.name}</code>
                            {row.env_key && (
                              <span>
                                .env: <code>{row.env_key}</code>
                              </span>
                            )}
                            {row.console_url && (
                              <a
                                href={row.console_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-0.5 text-[var(--color-primary)] hover:underline"
                              >
                                콘솔 <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                          {row.notes && (
                            <p className="mt-1.5 whitespace-pre-wrap text-xs text-[var(--color-text-sub)]">
                              {row.notes}
                            </p>
                          )}
                        </div>

                        {/* 발급일 */}
                        <div className="text-sm tabular-nums">
                          <span className="text-[11px] text-[var(--color-text-sub)] md:hidden">발급:</span>{' '}
                          {row.issued_at ?? <span className="text-[var(--color-text-sub)]">—</span>}
                        </div>
                        {/* 만료일 */}
                        <div className="text-sm tabular-nums">
                          <span className="text-[11px] text-[var(--color-text-sub)] md:hidden">만료:</span>{' '}
                          {row.expires_at ?? <span className="text-[var(--color-text-sub)]">—</span>}
                        </div>
                        {/* 남은 기간 + 주기 */}
                        <div className="text-sm">
                          <span
                            className={cn(
                              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                              exp.tone === 'fall' &&
                                'bg-[var(--color-fall)]/10 text-[var(--color-fall)]',
                              exp.tone === 'editor' &&
                                'bg-[var(--color-warn-bg)] text-[var(--color-warn)]',
                              exp.tone === 'sub' &&
                                'bg-[var(--color-bg)] text-[var(--color-text-sub)]',
                            )}
                          >
                            {exp.label}
                          </span>
                          {row.rotation_cycle_days && (
                            <div className="mt-1 text-[11px] text-[var(--color-text-sub)]">
                              권장 주기 {row.rotation_cycle_days}일
                            </div>
                          )}
                        </div>

                        {/* 관리 버튼 */}
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            className="rounded p-1.5 text-[var(--color-text-sub)] hover:bg-[var(--color-bg)]"
                            onClick={() => startEdit(row)}
                            aria-label="수정"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="rounded p-1.5 text-[var(--color-fall)] hover:bg-[var(--color-fall)]/10"
                            onClick={() => onDelete(row)}
                            aria-label="삭제"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── 폼 필드 헬퍼 ──────────────────────────────────────────────────────
interface FieldTextProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'date' | 'number';
  placeholder?: string;
  required?: boolean;
}
function FieldText({ label, value, onChange, type = 'text', placeholder, required }: FieldTextProps) {
  return (
    <div>
      <label className="mb-1 block text-xs text-[var(--color-text-sub)]">
        {label}
        {required && <span className="ml-0.5 text-[var(--color-fall)]">*</span>}
      </label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
